import { ArmorSubtype } from '../types/item';
import type { CharacterClassId } from '../types/character';

/**
 * Clases de armadura (Tela / Cuero / Metal) y qué puede vestir cada clase de personaje.
 *
 * Los 3 tipos mapean 1:1 al `ArmorSubtype` que ya existía:
 *   - **Tela**  = `CLOTH`  (ligera): la que menos defiende; para clases mágicas.
 *   - **Cuero** = `LIGHT`  (media): defiende menos que metal, sin penalización.
 *   - **Metal** = `HEAVY`  (pesada): más defensa física pero PENALIZA velocidad de movimiento.
 *
 * Acceso por **jerarquía**: cada clase tiene un tipo MÁXIMO y puede equipar ese tipo y todos los
 * más livianos (un Guerrero de Metal también puede usar Cuero y Tela; un Mago sólo Tela). El gate
 * se aplica al equipar (server) — ver `InventoryService.validateEquip`. Data-driven: no hace falta
 * poblar `requiredClasses` en cada item de armadura.
 *
 * Crafteo (fase futura): Metal/Cuero se forjan en **Herrería**; Tela se confecciona en **Sastrería**
 * (`skill_tailoring`). Ver docs/SMITHING_TIERS.md.
 */

/** Peso relativo de cada tipo de armadura (para la jerarquía de acceso). */
export const ARMOR_WEIGHT: Record<ArmorSubtype, number> = {
  [ArmorSubtype.CLOTH]: 0, // Tela
  [ArmorSubtype.LIGHT]: 1, // Cuero
  [ArmorSubtype.HEAVY]: 2, // Metal
};

/** Nombre visible (español) de cada tipo de armadura. */
export const ARMOR_TYPE_LABEL_ES: Record<ArmorSubtype, string> = {
  [ArmorSubtype.CLOTH]: 'Tela',
  [ArmorSubtype.LIGHT]: 'Cuero',
  [ArmorSubtype.HEAVY]: 'Metal',
};

/**
 * Tipo de armadura MÁS PESADO que cada clase puede equipar (puede usar ese y los más livianos).
 * Buckets: tanques/melee → Metal; híbridos/ágiles → Cuero; magos puros → sólo Tela.
 */
export const ARMOR_MAX_BY_CLASS: Record<CharacterClassId, ArmorSubtype> = {
  // Metal (y Cuero + Tela)
  warrior: ArmorSubtype.HEAVY,
  paladin: ArmorSubtype.HEAVY,
  pirate: ArmorSubtype.HEAVY,
  engineer: ArmorSubtype.HEAVY,
  // Cuero (y Tela)
  hunter: ArmorSubtype.LIGHT,
  rogue: ArmorSubtype.LIGHT,
  archer: ArmorSubtype.LIGHT,
  bard: ArmorSubtype.LIGHT,
  bandit: ArmorSubtype.LIGHT,
  explorer: ArmorSubtype.LIGHT,
  beastmaster: ArmorSubtype.LIGHT,
  monk: ArmorSubtype.LIGHT,
  shaman: ArmorSubtype.LIGHT,
  cleric: ArmorSubtype.LIGHT,
  // Sólo Tela (clases mágicas puras)
  wizard: ArmorSubtype.CLOTH,
  warlock: ArmorSubtype.CLOTH,
  necromancer: ArmorSubtype.CLOTH,
  druid: ArmorSubtype.CLOTH,
  priest: ArmorSubtype.CLOTH,
};

/**
 * True si `classId` puede equipar una armadura de `subtype` (su tipo o más liviano).
 * Si el subtype no es un tipo de armadura conocido, no restringe (devuelve true).
 */
export function canClassWearArmor(classId: CharacterClassId, subtype: string): boolean {
  const w = ARMOR_WEIGHT[subtype as ArmorSubtype];
  if (w === undefined) return true; // no es armadura tipada → sin restricción
  const max = ARMOR_MAX_BY_CLASS[classId];
  if (max === undefined) return true; // clase desconocida → no bloquear
  return w <= ARMOR_WEIGHT[max];
}

/** Multiplicador de `physDef` por tipo de armadura (sobre la curva base de Metal). */
export const ARMOR_PHYSDEF_MULT: Record<ArmorSubtype, number> = {
  [ArmorSubtype.HEAVY]: 1.0,
  [ArmorSubtype.LIGHT]: 0.6,
  [ArmorSubtype.CLOTH]: 0.35,
};

/** Penalización de velocidad de movimiento del Metal (moveSpeedPercent). Cuero/Tela = 0. */
export const METAL_MOVESPEED_PENALTY = -0.08;

/** Escala una physDef base (curva de Metal) al tipo dado, con mínimo 1. */
export function scaledPhysDef(baseHeavy: number, subtype: ArmorSubtype): number {
  return Math.max(1, Math.round(baseHeavy * ARMOR_PHYSDEF_MULT[subtype]));
}
